import React, { useReducer, useEffect, useMemo, useState } from "react";
import { InputText } from "../../_components/InputText/InputText";
import { TextField } from "@mui/material";
import styles from "../../_components/Styles/BaseStyleForAdminPanel.module.css";
import {RightWithPositionsAndUsers, UpdateRight} from "../../../../types/api/authorization/right.types";
import {useFetchAllFullInfoUser} from "../../../../hooks/authorization/useFetchAllFullInfoUser";
import {useFetchAllPositions} from "../../../../hooks/authorization/useFetchAllPositions";
import {getFullName} from "../../../../utils/nameFormatter";
import OpenCheckboxList from "../../_components/OpenCheckboxList/OpenCheckboxList";


interface UpdateRightModalProps {
    right: RightWithPositionsAndUsers
    onClose: () => void;
    onSave: (rightData: UpdateRight) => void;
}

const initialState: UpdateRight = {
    name: "",
    tag: "",
    comment: "",
    id: "",
    positions: [],
    users: []
}

type Action =
    | { type: "SET_FIELD", name: keyof UpdateRight, value: any }
    | { type: "SET_FIELDS"; fields: UpdateRight }

const reducer = (state: UpdateRight, action: Action) => {
    switch (action.type) {
        case "SET_FIELD":
            return { ...state, [action.name]: action.value } as UpdateRight;
        case "SET_FIELDS":
            return {...state, ...action.fields} as UpdateRight;
        default:
            return state;
    }
};

export const UpdateRightModal = ({ right, onClose, onSave }: UpdateRightModalProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const {
        data: usersData
    } = useFetchAllFullInfoUser();

    const {
        data: positionsData
    } = useFetchAllPositions();

    useEffect(() => {
        if (right){
            dispatch({
                type: "SET_FIELDS",
                fields: {
                    name: right.title,
                    tag: right.tag,
                    comment: right.comment,
                    positions: right.positionRights.map(x=>x.id) || [],
                    users: right.userRights.map(x=>x.id) || [],
                    id: right.id
                },
            });
        }
    }, [right])


    const handleValue = (event: any) => {
        dispatch({ type: "SET_FIELD", name: event.target.name, value: event.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(state);
        onClose();
    };

    const [posSearch, setPosSearch] = useState<string>("");
    const [userSearch, setUserSearch] = useState<string>("");

    const filteredPositions = useMemo(() => {
        const q = posSearch.trim().toLowerCase();
        if (!q) return positionsData ?? [];
        return (positionsData ?? []).filter(p => (p.title ?? "").toLowerCase().includes(q));
    }, [posSearch, positionsData]);

    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q) return usersData ?? [];
        return (usersData ?? []).filter(u => getFullName(u).toLowerCase().includes(q));
    }, [userSearch, usersData]);

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderContent}>
                        <h2 className={styles.modalTitle}>Обновление прав</h2>
                        <p className={styles.modalSubtitle}>Измените данные и сохраните изменения</p>
                    </div>
                    <button
                        type="button"
                        className={styles.modalClose}
                        onClick={onClose}
                        aria-label="Закрыть"
                    >
                        ×
                    </button>
                </div>
                <hr className={styles.modalDivider} />

                <form onSubmit={handleSubmit}>
                    <div className={styles.modalBody}>
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>Основная информация</h3>
                            </div>
                            <div className={styles.inputsGrid}>
                                <InputText
                                    name="name"
                                    value={state.name}
                                    handleValue={handleValue}
                                    label="Название"
                                    idField="name"
                                    required={true}
                                />
                                <InputText
                                    name="tag"
                                    value={state.tag}
                                    handleValue={handleValue}
                                    label="Тэг"
                                    idField="tag"
                                    required={true}
                                />
                            </div>
                            <hr className={styles.sectionDivider} />
                        </div>

                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>Описание</h3>
                            </div>
                            <div className={styles.inputsGrid}>
                                <div className={styles.fullRow}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        name="comment"
                                        id="comment"
                                        label="Комментарий"
                                        required
                                        value={state.comment}
                                        onChange={handleValue}
                                        multiline
                                        minRows={3}
                                        InputLabelProps={{sx: {color: '#64748b', '&.Mui-focused': {color: '#22c55e',}}}}
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                borderRadius: "14px",
                                                backgroundColor: "#FFFFFF",
                                                border: "1px solid #E5E7EB",
                                                transition: 'all 0.2s ease',
                                                padding: 0,
                                                '& .MuiInputBase-input': {
                                                    padding: '12px 16px',
                                                },
                                                '&:hover': {borderColor: "#d1d5db"},
                                                '&.Mui-focused': {
                                                    borderColor: "#22C55E",
                                                    boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.15)',
                                                }
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                            <hr className={styles.sectionDivider} />
                        </div>

                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>Назначение</h3>
                            </div>
                            <div className={styles.inputsGrid}>
                                <OpenCheckboxList
                                    label="Должности"
                                    name="positions"
                                    options={(filteredPositions ?? []).map(p => ({ value: p.id, label: p.title }))}
                                    selected={state.positions as string[]}
                                    onChange={handleValue}
                                    searchValue={posSearch}
                                    onSearchChange={setPosSearch}
                                />
                                <OpenCheckboxList
                                    label="Сотрудники"
                                    name="users"
                                    options={(filteredUsers ?? []).map(u => ({ value: u.id, label: getFullName(u) }))}
                                    selected={state.users as string[]}
                                    onChange={handleValue}
                                    searchValue={userSearch}
                                    onSearchChange={setUserSearch}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            className={`${styles.buttonBase} ${styles.buttonSecondary}`}
                            onClick={onClose}
                        >
                            Отмена
                        </button>

                        <button
                            type="submit"
                            className={`${styles.buttonBase} ${styles.buttonPrimary}`}
                        >
                            Обновить
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdateRightModal;

